import { createElement } from "react";
import { renderToStaticMarkup } from "react-dom/server";
// Type-only import: erased at build time so this utility carries no runtime
// dependency on the (heavy) Icon/config module graph. The caller resolves the
// record-type default icon and passes the component in.
import type { IconLike } from "components/Icon/Icon";

// The static app-default favicon shipped in index.html. Used as the final
// fallback when the opened record has neither a user-defined icon nor a
// record-type default icon.
const DEFAULT_FAVICON_HREF = "/favicon.ico";

// Colour used to tint the record-type default (mdi-react SVG) icons when they
// are rasterised into a favicon. mdi icons otherwise render with `currentColor`,
// which is meaningless in a detached data-URI. Uses the Cloudomation brand
// primary-blue (#00418c — the `primary-blue` token in
// assets/scss/abstracts/variables/_colors.scss). The same tint is applied to a
// user-chosen icon when it is a single-colour SVG, so those match the built-in
// icons; multi-colour SVGs and raster (e.g. uploaded PNG) container icons are
// used as-is.
const DEFAULT_ICON_COLOR = "#00418c";

// SVG namespace. renderToStaticMarkup of an mdi-react (or any JSX) <svg> does
// NOT emit an `xmlns` attribute — that is fine for an SVG inlined in the live
// DOM (the HTML parser assumes the SVG namespace inside <svg>), but a
// STANDALONE SVG loaded as an image (a `data:image/svg+xml,...` favicon href)
// is parsed as an XML document and MUST declare its namespace or it fails to
// parse -> the browser shows its built-in "no icon" fallback. This missing
// xmlns was the root cause of the per-record favicon regression.
const SVG_NS = "http://www.w3.org/2000/svg";

const isDataUri = (value: unknown): value is string =>
    typeof value === "string" && value.startsWith("data:");

// Normalise the serialised SVG markup so it is valid as a standalone favicon
// image: (1) ensure the SVG namespace is declared on the root element, and
// (2) ensure width/height are absolute pixel numbers. A favicon renders with
// no CSS/font context, so relative units (e.g. mdi's default `1em`, or any
// non-numeric value) collapse to zero -> a blank/invisible icon. Only the
// opening <svg ...> tag is touched; the path children are left untouched.
const normalizeSvgMarkup = (markup: string): string =>
    markup.replace(/<svg\b([^>]*)>/i, (_full, attrs: string): string => {
        let a = attrs;
        if (!/\bxmlns\s*=/.test(a)) {
            a = ` xmlns="${SVG_NS}"${a}`;
        }
        // Coerce any non-numeric width/height (e.g. "1em") to a fixed pixel size.
        a = a.replace(
            /\b(width|height)\s*=\s*"([^"]*)"/gi,
            (whole, name: string, val: string): string =>
                /^\d+(\.\d+)?$/.test(val) ? whole : `${name}="64"`
        );
        if (!/\bwidth\s*=/.test(a)) a += ' width="64"';
        if (!/\bheight\s*=/.test(a)) a += ' height="64"';
        return `<svg${a}>`;
    });

const getFaviconLinkElement = (): HTMLLinkElement => {
    let link = document.head.querySelector<HTMLLinkElement>('link[rel~="icon"]');
    if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
    }
    return link;
};

// Serialise an mdi-react / component icon to an inline SVG data-URI usable as a
// <link rel="icon"> href. Returns null if the component does not render an SVG
// (defensive — falls back to the app default).
const iconComponentToDataUri = (
    icon: IconLike,
    color: string = DEFAULT_ICON_COLOR
): string | null => {
    try {
        const markup = renderToStaticMarkup(
            // mdi-react components accept `color` and `size`; a plain
            // ComponentType<{className}> ignores them harmlessly.
            createElement(icon as React.ComponentType<{ color?: string; size?: number }>, {
                color,
                size: 64,
            })
        );
        if (!markup.includes("<svg")) {
            return null;
        }
        return `data:image/svg+xml,${encodeURIComponent(normalizeSvgMarkup(markup))}`;
    } catch {
        return null;
    }
};

// --- Single-colour user-SVG recolour ------------------------------------
// A user-chosen icon stored on a record/object-template is used as-is when it is
// a raster image or a genuinely multi-colour SVG. But a SINGLE-COLOUR (monochrome)
// SVG — e.g. the custom glyphs on many operator-* object templates — is tinted to
// DEFAULT_ICON_COLOR so it matches the built-in record-type favicons. Detection is
// deliberately conservative: anything that is not provably single-colour is left
// untouched, so an intentional multi-colour logo is never wrecked.

// Colour keywords that are not concrete paint colours (never counted toward the
// multi-colour test; `currentColor` is handled separately as a recolour target).
const NON_PAINT_KEYWORDS = new Set(["none", "transparent", "inherit", "currentcolor"]);

const isTransparentColor = (value: string): boolean => {
    const v = value.trim().toLowerCase();
    if (v === "transparent") return true;
    const fn = v.match(/^(?:rgba|hsla)\(([^)]*)\)$/);
    if (fn) {
        const parts = fn[1].split(/[,/\s]+/).filter(Boolean);
        if (parts.length >= 4 && parseFloat(parts[3]) === 0) return true;
    }
    if (/^#[0-9a-f]{8}$/.test(v) && v.slice(7) === "00") return true;
    if (/^#[0-9a-f]{4}$/.test(v) && v.slice(4) === "0") return true;
    return false;
};

// Is `value` a concrete paint colour — something that counts toward the
// multi-colour test and would be replaced by the tint? Excludes keywords,
// url(...) paint-server references (gradients are judged by their stop colours)
// and fully-transparent colours.
const isConcretePaintColor = (value: string): boolean => {
    const v = value.trim().toLowerCase();
    if (!v || NON_PAINT_KEYWORDS.has(v) || v.startsWith("url(")) return false;
    return !isTransparentColor(v);
};

// Matches fill / stroke / stop-color as an ATTRIBUTE (fill="…") — `\b` before the
// name plus the `=` after it means fill-opacity / stroke-width never match.
const PAINT_ATTR_RE = /\b(fill|stroke|stop-color)\s*=\s*(["'])([^"']*)\2/gi;
// Matches the same as a CSS DECLARATION (fill:… in style="" or a <style> block).
const PAINT_DECL_RE = /\b(fill|stroke|stop-color)(\s*:\s*)([^;{}"']+)/gi;

const collectConcreteColors = (markup: string): Set<string> => {
    const colors = new Set<string>();
    for (const m of markup.matchAll(PAINT_ATTR_RE)) {
        if (isConcretePaintColor(m[3])) colors.add(m[3].trim().toLowerCase());
    }
    for (const m of markup.matchAll(PAINT_DECL_RE)) {
        if (isConcretePaintColor(m[3])) colors.add(m[3].trim().toLowerCase());
    }
    return colors;
};

const decodeSvgDataUri = (dataUri: string): string | null => {
    const m = dataUri.match(/^data:image\/svg\+xml([^,]*),(.*)$/is);
    if (!m) return null;
    const isBase64 = /;base64/i.test(m[1] ?? "");
    const payload = m[2] ?? "";
    try {
        if (isBase64) {
            const bin =
                typeof atob === "function"
                    ? atob(payload)
                    : Buffer.from(payload, "base64").toString("binary");
            try {
                return new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)));
            } catch {
                return bin;
            }
        }
        return decodeURIComponent(payload);
    } catch {
        try {
            return decodeURIComponent(payload);
        } catch {
            return null;
        }
    }
};

// Rewrite every concrete paint colour (and currentColor) in the markup to `color`.
// Only recolour tokens that are actual colours — `none`/`transparent`/url(...) are
// left intact so a fill="none" stroked icon keeps its transparent fill.
const applyMonochromeTint = (markup: string, color: string): string => {
    const isRecolourable = (v: string): boolean =>
        isConcretePaintColor(v) || v.trim().toLowerCase() === "currentcolor";
    let out = markup
        .replace(PAINT_ATTR_RE, (whole, name, q, val) =>
            isRecolourable(val) ? `${name}=${q}${color}${q}` : whole
        )
        .replace(PAINT_DECL_RE, (whole, name, sep, val) =>
            isRecolourable(val) ? `${name}${sep}${color}` : whole
        )
        .replace(/currentColor/gi, color);
    // A bare icon with no fill/stroke at all renders with the default black fill;
    // give the root <svg> an explicit tint so it becomes brand-coloured.
    if (!/\b(fill|stroke)\s*[=:]/i.test(markup)) {
        out = out.replace(/<svg\b([^>]*)>/i, (_full, attrs) => `<svg${attrs} fill="${color}">`);
    }
    return out;
};

// If `dataUri` is a single-colour SVG data-URI, return a copy tinted to `color`
// (as a normalised, favicon-valid SVG data-URI). Otherwise return null — the
// caller then uses the original icon as-is. Returns null for non-SVG data-URIs,
// SVGs embedding a raster <image>, multi-colour SVGs, or on any decode failure.
export const recolorSingleColorSvgFavicon = (dataUri: string, color: string): string | null => {
    const markup = decodeSvgDataUri(dataUri);
    if (markup === null) return null;
    // An embedded raster can't be recoloured — leave the icon untouched.
    if (/<image\b/i.test(markup)) return null;
    if (collectConcreteColors(markup).size >= 2) return null;
    const tinted = applyMonochromeTint(markup, color);
    return `data:image/svg+xml,${encodeURIComponent(normalizeSvgMarkup(tinted))}`;
};

// Resolve the favicon href for the currently-opened record, honouring the
// fallback chain: (1) a user-defined icon stored on the record itself
// (containers such as workspace/project/bundle store an uploaded image as a
// data-URI on `icon`), then (2) the record-type default icon (an mdi-react SVG
// component the caller resolved via getByRecordTypeName), then (3) the app
// default favicon.
// `curatedIcon` is the component the caller resolved from a `mdi:<key>` curated
// icon reference stored on the record (favicon.ts deliberately carries no
// runtime dependency on the Icon/IconCollection module graph, so the caller
// does the key→component lookup). A user-chosen curated glyph is rendered like
// the record-type default — a brand-tinted standalone SVG — but takes
// precedence over it, since it is an explicit user choice.
// A user-uploaded data-URI icon wins over everything: a single-colour SVG is
// tinted brand-blue to match the built-in icons, while a raster or multi-colour
// SVG is used exactly as uploaded.
export const resolveRecordFaviconHref = (
    userIcon: unknown,
    typeDefaultIcon: IconLike | undefined,
    curatedIcon?: IconLike
): string => {
    if (isDataUri(userIcon)) {
        return recolorSingleColorSvgFavicon(userIcon, DEFAULT_ICON_COLOR) ?? userIcon;
    }
    const chosenIcon = curatedIcon ?? typeDefaultIcon;
    if (chosenIcon) {
        const dataUri = iconComponentToDataUri(chosenIcon);
        if (dataUri) {
            return dataUri;
        }
    }
    return DEFAULT_FAVICON_HREF;
};

const applyFaviconHref = (href: string): void => {
    const link = getFaviconLinkElement();
    if (link.getAttribute("href") === href) {
        return;
    }
    link.setAttribute("href", href);
    if (href.startsWith("data:image/svg")) {
        link.setAttribute("type", "image/svg+xml");
    } else if (href.startsWith("data:image/")) {
        const match = href.match(/^data:(image\/[a-zA-Z0-9.+-]+)/);
        link.setAttribute("type", match ? match[1] : "image/png");
    } else {
        link.removeAttribute("type");
    }
};

// Set the browser-tab favicon to the icon of the currently-opened record.
// `userIcon` is the record's own icon field (a data-URI for containers, else
// absent); `typeDefaultIcon` is the record-type default icon component.
export const applyRecordFavicon = (
    userIcon: unknown,
    typeDefaultIcon: IconLike | undefined,
    curatedIcon?: IconLike
): void => {
    applyFaviconHref(resolveRecordFaviconHref(userIcon, typeDefaultIcon, curatedIcon));
};

// Restore the app-default favicon (no single record open — list/search/trash).
export const resetFavicon = (): void => {
    applyFaviconHref(DEFAULT_FAVICON_HREF);
};
